import { useEffect } from 'react';
import { FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { describeApiError } from '../../../shared/api/errors';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createEmployeeFaltasApi } from '../faltas/api';
import type { FaltaGlobalListItem } from './types';

const schema = z.object({
  colaboradorCodigo: z
    .number({ invalid_type_error: 'Selecione um colaborador' })
    .int()
    .positive('Selecione um colaborador'),
  dataFalta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (use AAAA-MM-DD)'),
  justificada: z.boolean(),
  motivo: z.string().max(500, 'Máximo 500 caracteres'),
});

type FormValues = z.infer<typeof schema>;

export interface FaltaGlobalFormDialogProps {
  open: boolean;
  /** Linha em edição, ou `null` para nova falta. */
  falta: FaltaGlobalListItem | null;
  onClose: () => void;
}

/**
 * Cria/edita uma Falta a partir da visão global. Diferente do form aninhado,
 * aqui o colaborador é escolhido no próprio dialog (nova falta) — o submit
 * chama o sub-recurso `/employees/{colaboradorCodigo}/faltas`. Em edição o
 * colaborador e a data ficam travados (PK = dataFalta; trocar data = del+add).
 */
export function FaltaGlobalFormDialog({
  open,
  falta,
  onClose,
}: FaltaGlobalFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = falta !== null;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      colaboradorCodigo: 0,
      dataFalta: new Date().toISOString().slice(0, 10),
      justificada: false,
      motivo: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const nested = createEmployeeFaltasApi(values.colaboradorCodigo);
      const input = {
        dataFalta: values.dataFalta,
        justificada: values.justificada,
        motivo: values.motivo,
      };
      if (isEdit) return nested.update(values.dataFalta, input);
      return nested.create(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['faltas', 'global'] });
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    mutation.reset();
    if (falta) {
      form.reset({
        colaboradorCodigo: falta.employeeId,
        dataFalta: falta.dataFalta,
        justificada: falta.justificada,
        motivo: falta.motivo ?? '',
      });
    } else {
      form.reset({
        colaboradorCodigo: 0,
        dataFalta: new Date().toISOString().slice(0, 10),
        justificada: false,
        motivo: '',
      });
    }
    // mutation é estável (react-query); reset limpa erro anterior ao reabrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, falta, form]);

  const serverError = mutation.isError ? describeApiError(mutation.error) : null;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      await mutation.mutateAsync(values);
    } catch {
      /* erro exibido via serverError (mutation.error) */
    }
  };

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.faltasGlobal.dialog.editTitle')
          : t('features.colaboradores.faltasGlobal.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        {isEdit ? (
          <Typography variant="subtitle2">{falta?.colaboradorNome}</Typography>
        ) : (
          <Controller
            control={form.control}
            name="colaboradorCodigo"
            render={({ field, fieldState }) => (
              <ColaboradorSelect
                value={field.value > 0 ? field.value : null}
                onChange={(id) => field.onChange(id ?? 0)}
                label={t('features.colaboradores.faltasGlobal.fields.colaborador')}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}

        <DatePickerPtBrField
          control={form.control}
          name="dataFalta"
          label={t('features.colaboradores.faltas.fields.dataFalta')}
          disabled={isEdit}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: isEdit
                ? t('features.colaboradores.faltas.fields.dataFaltaLockedHelper')
                : t('features.colaboradores.faltas.fields.dataFaltaHelper'),
            },
          }}
        />

        <Controller
          control={form.control}
          name="justificada"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={t('features.colaboradores.faltas.fields.justificada')}
            />
          )}
        />

        <TextField
          label={t('features.colaboradores.faltas.fields.motivo')}
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          inputProps={{ maxLength: 500 }}
          error={Boolean(form.formState.errors.motivo)}
          helperText={
            form.formState.errors.motivo?.message ??
            t('features.colaboradores.faltas.fields.motivoHelper')
          }
          {...form.register('motivo')}
        />
      </Stack>
    </FormDialog>
  );
}
